import type {
  ComponentPropsWithoutRef,
  ElementRef,
  ForwardedRef,
  HTMLAttributes,
} from 'react'
import { forwardRef, useCallback, useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { XIcon } from '@phosphor-icons/react'
import * as DialogPrimitive from '@radix-ui/react-dialog'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'data-[state=open]:duration-base data-[state=closed]:duration-fast fixed inset-0 z-50 bg-black/30 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * Send focus back to whatever opened the dialog once it closes.
 *
 * Radix restores focus itself, but only while the node it saved is still
 * focusable at close time. The toolbars and table headers these dialogs open
 * from re-render while the dialog is up, so that restore silently drops focus
 * on the body and the next Tab starts again from the top of the page.
 *
 * The opener cannot be read when this component mounts: a dialog is rendered
 * with `open={false}` for as long as its page is on screen, so mount time is
 * page-load time, not open time. It is read when the content element is
 * attached instead, which happens on open and before Radix's own focus scope
 * has moved focus anywhere. Everything focused so far is kept in a short
 * history, so an opener that has itself been re-rendered away still resolves
 * to something sensible behind it.
 */
const FOCUS_HISTORY_LIMIT = 20
const focusHistory: HTMLElement[] = []
let isTrackingFocus = false

function trackFocusHistory() {
  if (isTrackingFocus || typeof document === 'undefined') return
  isTrackingFocus = true

  document.addEventListener(
    'focusin',
    event => {
      const target = event.target as HTMLElement | null
      if (
        !target ||
        target === document.body ||
        typeof target.focus !== 'function'
      ) {
        return
      }
      const existing = focusHistory.indexOf(target)
      if (existing !== -1) focusHistory.splice(existing, 1)
      focusHistory.push(target)
      if (focusHistory.length > FOCUS_HISTORY_LIMIT) focusHistory.shift()
    },
    true,
  )
}

function findOpener(content: HTMLElement | null) {
  for (let index = focusHistory.length - 1; index >= 0; index -= 1) {
    const candidate = focusHistory[index]
    if (!candidate.isConnected) continue
    if (content && content.contains(candidate)) continue
    return candidate
  }
  return null
}

function useOpenerFocusReturn(
  forwardedRef: ForwardedRef<ElementRef<typeof DialogPrimitive.Content>>,
) {
  useEffect(trackFocusHistory, [])

  const openerRef = useRef<HTMLElement | null>(null)

  const setContentRef = useCallback(
    (node: ElementRef<typeof DialogPrimitive.Content> | null) => {
      if (node) {
        openerRef.current = findOpener(node)
      }

      if (typeof forwardedRef === 'function') {
        forwardedRef(node)
      } else if (forwardedRef) {
        forwardedRef.current = node
      }
    },
    [forwardedRef],
  )

  const returnFocusToOpener = useCallback((event: Event) => {
    const content = (event.currentTarget ?? event.target) as HTMLElement | null
    const opener =
      openerRef.current && openerRef.current.isConnected
        ? openerRef.current
        : findOpener(content)

    openerRef.current = null
    if (!opener) return

    event.preventDefault()
    opener.focus({ preventScroll: true })
  }, [])

  return { setContentRef, returnFocusToOpener }
}

const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideCloseButton?: boolean
  }
>(({ className, children, hideCloseButton = false, ...props }, ref) => {
  const { setContentRef, returnFocusToOpener } = useOpenerFocusReturn(ref)

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={setContentRef}
        // Radix emits role="dialog" but not aria-modal, so screen readers treat
        // the page behind an open dialog as reachable. Callers that render a
        // non-modal dialog can override it through props.
        aria-modal="true"
        onCloseAutoFocus={returnFocusToOpener}
        className={cn(
          'data-[state=open]:duration-base data-[state=open]:ease-ios data-[state=closed]:duration-fast data-[state=closed]:ease-ios-in fixed left-[50%] top-[50%] z-50 flex max-h-[calc(100vh-2rem)] w-[calc(100%-2rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] flex-col overflow-y-auto rounded-lg border bg-background p-6 shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:max-h-[90vh] sm:w-full',
          className,
        )}
        {...props}
      >
        {children}
        {!hideCloseButton && (
          <DialogPrimitive.Close className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-sm border bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
            <XIcon className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

// Kept as a name-only alias: this used to be a byte-for-byte copy of
// DialogContent, which meant every dismiss or accessibility fix had to be made
// twice.
const StyledDialogContent = DialogContent

const DialogHeader = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left',
      className,
    )}
    {...props}
  />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      // Pin to the bottom of the (scrollable) DialogContent with a solid
      // background so long bodies scroll *under* the actions instead of the
      // buttons overlapping the content. No effect in dialogs that don't scroll.
      'sticky bottom-0 z-10 mt-2 flex flex-col-reverse bg-background pt-3 sm:flex-row sm:justify-end sm:space-x-2',
      className,
    )}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className,
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  StyledDialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}

// Re-export DialogPattern to provide a single import point
export { DialogPattern } from './dialog-pattern'
